import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bot, Clock, Users, DollarSign, Mic, Globe, Monitor, Shield, Zap, Heart, Phone, MessageSquare, CheckCircle, Star, ArrowRight, Cpu, Headphones, Languages, TouchpadIcon, UserCheck, BrainCircuit, CalendarOff, PiggyBank, Building2, ShoppingBag, Calculator, ClipboardList, Mail, CalendarCheck, FileText, Briefcase, MessagesSquare, BookOpen, Stethoscope, GraduationCap, Wrench, TruckIcon, Scale, Utensils, Hotel, Scissors, Dumbbell, Landmark, ChevronLeft, ChevronRight, X } from 'lucide-react';

function Bornii() {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    entreprise: '',
    message: '',
    source: 'bornii'
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);
  const employeSectionRef = useRef(null);

  // Chatbot states
  const [showChatbot, setShowChatbot] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatStep, setChatStep] = useState('welcome');
  const [isTyping, setIsTyping] = useState(false);
  const [chatbotDismissed, setChatbotDismissed] = useState(false);
  const [chatbotTriggered, setChatbotTriggered] = useState(false);
  const [showChatbotModal, setShowChatbotModal] = useState(false);
  const [modalMessages, setModalMessages] = useState([]);
  const [isModalTyping, setIsModalTyping] = useState(false);
  const chatModalMessagesRef = useRef(null);
  const chatMessagesRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    axios.post(`${API_URL}/api/link-clicks/bornii`, {
      language: navigator.language,
      platform: navigator.userAgentData?.platform || navigator.platform,
      screen_width: window.screen.width,
      screen_height: window.screen.height
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/leads/public`, formData);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue. Veuillez reessayer.');
    } finally {
      setLoading(false);
    }
  };

  const currentDate = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const scrollToForm = () => {
    // Fermer les fenetres de chat
    setShowChatbot(false);
    setShowChatbotModal(false);
    sessionStorage.setItem('bornii_modal_dismissed', 'true');
    // Scroll directement vers le formulaire (pas la section avec WhatsApp)
    setTimeout(() => {
      const el = document.getElementById('bornii-form-card');
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset - 20;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 300);
  };

  // Carousel auto-play
  const totalSlides = 16;
  const nextSlide = useCallback(() => {
    setCarouselIndex(prev => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCarouselIndex(prev => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const goToSlide = useCallback((index) => {
    setCarouselIndex(index);
  }, []);

  // Auto-play every 4 seconds
  useEffect(() => {
    autoPlayRef.current = setInterval(() => {
      nextSlide();
    }, 4000);
    return () => clearInterval(autoPlayRef.current);
  }, [nextSlide]);

  // Pause auto-play on hover / touch
  const pauseAutoPlay = () => clearInterval(autoPlayRef.current);
  const resumeAutoPlay = () => {
    clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(() => {
      nextSlide();
    }, 4000);
  };

  // Chatbot conversation tree
  const chatTree = {
    welcome: {
      bot: "Bonjour ! Je suis Bornii, votre assistant IA. Comment puis-je vous aider aujourd'hui ?",
      options: [
        { label: "Je cherche un employe IA", next: 'employe_ia' },
        { label: "Je veux une borne IA", next: 'borne_ia' },
        { label: "Je veux en savoir plus", next: 'en_savoir_plus' }
      ]
    },
    employe_ia: {
      bot: "Excellent choix ! Un employe IA Bornii travaille 24h/24 pour seulement 159EUR/mois. Quel type de poste souhaitez-vous automatiser ?",
      options: [
        { label: "Service client / Support", next: 'employe_service' },
        { label: "Commercial / Vente", next: 'employe_commercial' },
        { label: "Administratif / Secretariat", next: 'employe_admin' },
        { label: "Autre poste", next: 'employe_autre' }
      ]
    },
    borne_ia: {
      bot: "La borne IA Bornii est une solution physique interactive avec ecran tactile et reconnaissance vocale. Dans quel secteur exercez-vous ?",
      options: [
        { label: "Commerce / Retail", next: 'borne_commerce' },
        { label: "Restauration / Hotellerie", next: 'borne_resto' },
        { label: "Sante / Bien-etre", next: 'borne_sante' },
        { label: "Autre secteur", next: 'borne_autre' }
      ]
    },
    en_savoir_plus: {
      bot: "Bornii propose deux solutions : un Employe IA virtuel (chatbot, standard telephonique, support...) et une Borne IA physique pour vos locaux. Le tout a partir de 159EUR/mois. Qu'est-ce qui vous interesse le plus ?",
      options: [
        { label: "L'employe IA virtuel", next: 'employe_ia' },
        { label: "La borne IA physique", next: 'borne_ia' },
        { label: "Les deux solutions", next: 'les_deux' },
        { label: "Voir les tarifs", next: 'tarifs' }
      ]
    },
    employe_service: {
      bot: "Parfait ! Notre IA peut gerer votre service client : reponses aux questions frequentes, gestion des reclamations, suivi de commandes... Elle repond en moins de 2 secondes, dans plus de 30 langues !",
      options: [
        { label: "Demander une demo gratuite", next: 'go_form' },
        { label: "Voir les tarifs", next: 'tarifs' }
      ]
    },
    employe_commercial: {
      bot: "Notre IA commerciale peut qualifier vos prospects, presenter vos offres, generer des devis et meme relancer automatiquement vos contacts. Elle ne rate jamais un lead !",
      options: [
        { label: "Demander une demo gratuite", next: 'go_form' },
        { label: "Voir les tarifs", next: 'tarifs' }
      ]
    },
    employe_admin: {
      bot: "L'IA administrative Bornii gere votre agenda, repond aux emails, classe vos documents et assure l'accueil telephonique. Fini le temps perdu sur les taches repetitives !",
      options: [
        { label: "Demander une demo gratuite", next: 'go_form' },
        { label: "Voir les tarifs", next: 'tarifs' }
      ]
    },
    employe_autre: {
      bot: "Bornii s'adapte a tous les metiers : comptable, RH, redacteur web, community manager, formateur, support technique... Dites-nous votre besoin et nous creerons votre IA sur mesure.",
      options: [
        { label: "Demander une demo gratuite", next: 'go_form' },
        { label: "En savoir plus sur les metiers", next: 'metiers_detail' }
      ]
    },
    borne_commerce: {
      bot: "En commerce, notre borne IA remplace une hotesse d'accueil ou un vendeur : elle oriente les clients, presente les produits, repond aux questions et peut meme encaisser. Disponible 24h/24 !",
      options: [
        { label: "Demander une demo gratuite", next: 'go_form' },
        { label: "Voir les tarifs", next: 'tarifs' }
      ]
    },
    borne_resto: {
      bot: "En restauration et hotellerie, la borne Bornii gere les commandes, les reservations, l'accueil multilingue et les recommandations. Vos clients sont servis instantanement !",
      options: [
        { label: "Demander une demo gratuite", next: 'go_form' },
        { label: "Voir les tarifs", next: 'tarifs' }
      ]
    },
    borne_sante: {
      bot: "En sante et bien-etre, notre borne accueille les patients, gere les prises de rendez-vous, fournit des informations et oriente vers le bon specialiste. Tout en respectant la confidentialite.",
      options: [
        { label: "Demander une demo gratuite", next: 'go_form' },
        { label: "Voir les tarifs", next: 'tarifs' }
      ]
    },
    borne_autre: {
      bot: "Notre borne IA s'installe dans tout type d'etablissement : administration, education, sport, beaute, immobilier... Elle s'adapte a votre activite et parle plus de 30 langues.",
      options: [
        { label: "Demander une demo gratuite", next: 'go_form' },
        { label: "Voir les tarifs", next: 'tarifs' }
      ]
    },
    les_deux: {
      bot: "Combiner les deux solutions est ideal ! L'employe IA gere vos canaux digitaux (chat, email, telephone) tandis que la borne IA accueille physiquement vos visiteurs. Une couverture complete 24h/24 !",
      options: [
        { label: "Demander une demo gratuite", next: 'go_form' },
        { label: "Voir les tarifs", next: 'tarifs' }
      ]
    },
    tarifs: {
      bot: "Nos tarifs commencent a 159EUR/mois pour un employe IA. C'est 10x moins cher qu'un employe traditionnel (1 800EUR/mois avec charges). La demo est gratuite et sans engagement !",
      options: [
        { label: "Demander ma demo gratuite", next: 'go_form' },
        { label: "Retour au debut", next: 'welcome' }
      ]
    },
    metiers_detail: {
      bot: "Voici quelques exemples : Service Client, Support en Ligne, Commercial, Comptable, Standardiste, Secretaire, Emailing, Gestionnaire RDV, Redacteur Web, Formateur, Assistant RH, Community Manager... Et bien plus !",
      options: [
        { label: "Demander une demo gratuite", next: 'go_form' },
        { label: "Retour au debut", next: 'welcome' }
      ]
    },
    go_form: {
      bot: "Super ! Remplissez notre formulaire de contact et un expert Bornii vous recontactera sous 24h pour une demonstration personnalisee gratuite. On s'occupe de tout !",
      options: [
        { label: "Aller au formulaire", next: 'scroll_to_form' },
        { label: "Recommencer", next: 'welcome' }
      ]
    }
  };

  // Add bot message with typing delay
  const addBotMessage = useCallback((text, options) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setChatMessages(prev => [...prev, { type: 'bot', text, options }]);
    }, 800 + Math.random() * 400);
  }, []);

  // Handle user option click
  const handleChatOption = useCallback((option) => {
    // Add user message
    setChatMessages(prev => [...prev, { type: 'user', text: option.label }]);

    if (option.next === 'scroll_to_form') {
      // Close chatbot and scroll to form
      setTimeout(() => {
        scrollToForm();
      }, 500);
      return;
    }

    const nextStep = chatTree[option.next];
    if (nextStep) {
      setChatStep(option.next);
      addBotMessage(nextStep.bot, nextStep.options);
    }
  }, [addBotMessage, chatTree]);

  // Scroll chat to bottom on new messages (widget)
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages, isTyping]);

  // Scroll modal chat to bottom
  useEffect(() => {
    if (chatModalMessagesRef.current) {
      chatModalMessagesRef.current.scrollTop = chatModalMessagesRef.current.scrollHeight;
    }
  }, [modalMessages, isModalTyping]);

  // Modal bot message with typing delay
  const addModalBotMessage = useCallback((text, options) => {
    setIsModalTyping(true);
    setTimeout(() => {
      setIsModalTyping(false);
      setModalMessages(prev => [...prev, { type: 'bot', text, options }]);
    }, 800 + Math.random() * 400);
  }, []);

  // Handle modal option click
  const handleModalOption = useCallback((option) => {
    setModalMessages(prev => [...prev, { type: 'user', text: option.label }]);

    if (option.next === 'scroll_to_form') {
      setTimeout(() => {
        scrollToForm();
      }, 500);
      return;
    }

    const nextStep = chatTree[option.next];
    if (nextStep) {
      addModalBotMessage(nextStep.bot, nextStep.options);
    }
  }, [addModalBotMessage, chatTree]);

  // IntersectionObserver for section-employe
  useEffect(() => {
    const alreadyShown = sessionStorage.getItem('bornii_chatbot_shown');
    if (alreadyShown) {
      setChatbotTriggered(true);
      if (sessionStorage.getItem('bornii_chatbot_dismissed')) {
        setChatbotDismissed(true);
      }
      return;
    }

    const section = employeSectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            sessionStorage.setItem('bornii_chatbot_shown', 'true');
            setChatbotTriggered(true);
            setShowChatbot(true);
            const welcome = chatTree.welcome;
            setChatMessages([{ type: 'bot', text: welcome.bot, options: welcome.options }]);

            // Also open the modal popup (only once per session)
            if (!sessionStorage.getItem('bornii_modal_dismissed')) {
              setShowChatbotModal(true);
              setModalMessages([{ type: 'bot', text: welcome.bot, options: welcome.options }]);
            }

            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  // Close chatbot widget
  const closeChatbot = () => {
    setShowChatbot(false);
    setChatbotDismissed(true);
    sessionStorage.setItem('bornii_chatbot_dismissed', 'true');
  };

  // Close modal popup
  const closeChatbotModal = () => {
    setShowChatbotModal(false);
    sessionStorage.setItem('bornii_modal_dismissed', 'true');
    // On mobile, also close the widget bubble
    if (window.innerWidth <= 768) {
      setShowChatbot(false);
    }
  };

  // Reopen chatbot (fab button)
  const reopenChatbot = () => {
    setChatbotDismissed(false);
    setShowChatbot(true);
    if (chatMessages.length === 0) {
      const welcome = chatTree.welcome;
      setChatMessages([{ type: 'bot', text: welcome.bot, options: welcome.options }]);
    }
  };

  // Touch swipe support
  const touchStartX = useRef(0);
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    pauseAutoPlay();
  };
  const handleTouchEnd = (e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextSlide();
      else prevSlide();
    }
    resumeAutoPlay();
  };

  const avantagesEmployeIA = [
    { icon: Clock, title: 'Disponible 24h/24, 7j/7', desc: 'Jamais absent, jamais en retard, jamais de conges' },
    { icon: Zap, title: 'Gain de temps considerable', desc: 'Repond instantanement a toutes les demandes' },
    { icon: UserCheck, title: 'Plus de soucis de recrutement', desc: 'Fini les entretiens, les essais, les deceptions' },
    { icon: Heart, title: 'Forme a votre image', desc: 'Votre ton, vos valeurs, votre identite de marque' },
    { icon: Shield, title: 'Ne vous quittera jamais', desc: 'Pas de demission, pas de preavis, fidelite absolue' },
    { icon: PiggyBank, title: 'Cout maitrise', desc: '159EUR/mois vs 1 800EUR/mois de SMIC charges' }
  ];

  const avantagesBorneIA = [
    { icon: Monitor, title: 'Ecran interactif tactile', desc: 'Interface intuitive et moderne pour vos visiteurs' },
    { icon: Mic, title: 'Reconnaissance vocale', desc: 'Interaction naturelle par la voix, comme avec un humain' },
    { icon: Languages, title: 'Multi-langues', desc: 'Accueillez vos clients dans leur langue maternelle' },
    { icon: Building2, title: 'Remplace un employe physique', desc: 'Hotesse d\'accueil, caissier, vendeur, conseiller' }
  ];

  const useCases = [
    { icon: Headphones, label: 'Hotesse d\'accueil' },
    { icon: DollarSign, label: 'Caissier' },
    { icon: Users, label: 'Vendeur' },
    { icon: BrainCircuit, label: 'Conseiller' },
    { icon: Phone, label: 'Standard telephonique' },
    { icon: Globe, label: 'Accueil multilingue' }
  ];

  const metiers = [
    { icon: Headphones, title: 'Service Client', desc: 'Repond aux questions, gere les reclamations et fidelise vos clients 24h/24', color: '#667eea', image: 'https://images.unsplash.com/photo-1556745753-b2904692b3cd?w=1200&h=800&fit=crop&q=80&crop=faces' },
    { icon: MessagesSquare, title: 'Support en Ligne', desc: 'Chat en direct, emails, tickets... votre IA gere tous les canaux de support', color: '#a855f7', image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&h=800&fit=crop&q=80&crop=faces' },
    { icon: ShoppingBag, title: 'Commercial', desc: 'Qualifie les prospects, presente vos offres et genere des devis automatiquement', color: '#43e97b', image: 'https://images.unsplash.com/photo-1549923746-c502d488b3ea?w=1200&h=800&fit=crop&q=80&crop=faces' },
    { icon: Calculator, title: 'Comptable', desc: 'Saisie de factures, suivi de tresorerie, relances impayees, rapports financiers', color: '#f59e0b', image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=800&fit=crop&q=80&crop=faces' },
    { icon: Phone, title: 'Standardiste', desc: 'Accueil telephonique, transfert d\'appels, prise de messages et de RDV', color: '#ec4899', image: '/standardiste.jpg' },
    { icon: ClipboardList, title: 'Secretaire', desc: 'Gestion d\'agenda, courriers, organisation de reunions, classement de documents', color: '#06b6d4', image: 'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?w=1200&h=800&fit=crop&q=80&crop=faces' },
    { icon: Mail, title: 'Charge d\'Emailing', desc: 'Campagnes email, newsletters, relances automatiques et segmentation clients', color: '#8b5cf6', image: 'https://images.unsplash.com/photo-1586880244406-556ebe35f282?w=1200&h=800&fit=crop&q=80&crop=faces' },
    { icon: CalendarCheck, title: 'Gestionnaire de RDV', desc: 'Prise de rendez-vous en ligne, rappels automatiques, gestion des annulations', color: '#10b981', image: 'https://images.unsplash.com/photo-1506784365847-bbad939e9335?w=1200&h=800&fit=crop&q=80&crop=faces' },
    { icon: FileText, title: 'Redacteur Web', desc: 'Articles de blog, fiches produits, contenus SEO et publications reseaux sociaux', color: '#f97316', image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=1200&h=800&fit=crop&q=80&crop=faces' },
    { icon: BookOpen, title: 'Formateur', desc: 'Tutorats interactifs, quiz, parcours de formation et onboarding collaborateurs', color: '#14b8a6', image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=1200&h=800&fit=crop&q=80&crop=faces' },
    { icon: Briefcase, title: 'Assistant RH', desc: 'Reponses aux questions des salaries, gestion des conges, processus de recrutement', color: '#6366f1', image: 'https://images.unsplash.com/photo-1565688527174-775059ac429c?w=1200&h=800&fit=crop&q=80&crop=faces' },
    { icon: Users, title: 'Community Manager', desc: 'Animation des reseaux sociaux, moderation des commentaires, interactions clients', color: '#e11d48', image: '/community-manager.jpg' },
    { icon: Scale, title: 'Assistant Juridique', desc: 'Recherche juridique, redaction de documents, veille reglementaire automatisee', color: '#0891b2', image: 'https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=1200&h=800&fit=crop&q=80&crop=faces' },
    { icon: TruckIcon, title: 'Logisticien', desc: 'Suivi des commandes, gestion des stocks, coordination des livraisons', color: '#84cc16', image: 'https://images.unsplash.com/photo-1553413077-190dd305871c?w=1200&h=800&fit=crop&q=80&crop=faces' },
    { icon: Wrench, title: 'Support Technique', desc: 'Diagnostic de pannes, guides de depannage, assistance technique niveau 1 et 2', color: '#ef4444', image: 'https://images.unsplash.com/photo-1581092921461-eab62e97a780?w=1200&h=800&fit=crop&q=80&crop=faces' },
    { icon: GraduationCap, title: 'Conseiller Orientation', desc: 'Guide les visiteurs, oriente vers les bons services, recommandations personnalisees', color: '#7c3aed', image: 'https://images.unsplash.com/photo-1551836022-aadb801c60ae?w=1200&h=800&fit=crop&q=80&crop=faces' }
  ];

  const secteurs = [
    { icon: ShoppingBag, label: 'Commerce & Retail' },
    { icon: Utensils, label: 'Restauration' },
    { icon: Hotel, label: 'Hotellerie' },
    { icon: Stethoscope, label: 'Sante & Bien-etre' },
    { icon: Scissors, label: 'Coiffure & Beaute' },
    { icon: Dumbbell, label: 'Sport & Fitness' },
    { icon: Landmark, label: 'Administration' },
    { icon: GraduationCap, label: 'Education' },
    { icon: Briefcase, label: 'Cabinet & Agence' },
    { icon: Building2, label: 'Immobilier' }
  ];

  if (success) {
    return (
      <div className="bornii-page">
        <style>{getStyles()}</style>
        <div className="bg-gradient"></div>
        <div className="bg-grid"></div>
        <div className="success-container">
          <div className="success-card">
            <div className="success-icon"><CheckCircle size={40} /></div>
            <h1>Demande envoyee !</h1>
            <p>Merci pour votre interet pour Bornii. Notre equipe vous contactera dans les plus brefs delais pour discuter de votre projet.</p>
            <div className="success-info">
              <strong>Prochaine etape</strong>
              <span>Un expert Bornii vous contactera sous 24h pour une demonstration personnalisee.</span>
            </div>
            <a href="/" className="back-btn">Retour a l'accueil</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bornii-page">
      <style>{getStyles()}</style>

      <div className="bg-gradient"></div>
      <div className="bg-grid"></div>

      {/* Floating Elements */}
      <div className="floating-elements">
        <div className="floating-circle c1"></div>
        <div className="floating-circle c2"></div>
        <div className="floating-circle c3"></div>
      </div>

      {/* Header */}
      <header className="bornii-header">
        <div className="logo">
          <div className="logo-icon"><Bot size={22} /></div>
          <span className="logo-text">Bornii</span>
          <span className="logo-sub">by E-Shops PRO</span>
        </div>
        <div className="date-text">{currentDate}</div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-layout">
          <div className="hero-content">
            <div className="badge">
              <span className="badge-dot"></span>
              <span>Innovation IA</span>
            </div>

            <h1 className="hero-title">
              <span className="title-white">Votre employe IA,</span>
              <br />
              <span className="title-gradient">disponible 24h/24</span>
            </h1>

            {/* Image mobile uniquement - entre titre et sous-titre */}
            <div className="hero-image-mobile">
              <img src="/bornii-borne.png" alt="Borne IA Bornii" />
            </div>

            <p className="hero-subtitle">
              Bornii remplace vos employes par une intelligence artificielle formee a votre image.
              Hotesse d'accueil, vendeur, caissier... votre IA travaille pour vous, sans jamais s'arreter.
            </p>

            <div className="hero-price">
              <span className="price-label">A partir de</span>
              <span className="price-amount">159<span className="price-currency">EUR</span></span>
              <span className="price-period">/mois</span>
            </div>

            <button className="cta-btn" onClick={scrollToForm}>
              <span>Demander une demo</span>
              <ArrowRight size={18} />
            </button>
          </div>

          <div className="hero-image">
            <div className="hero-image-glow"></div>
            <img src="/bornii-borne.png" alt="Borne IA Bornii - Borne interactive intelligente" />
            <div className="hero-image-badge">
              <Zap size={14} />
              <span>Votre IA</span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="hero-stats">
          <div className="stat-item">
            <div className="stat-number">24/7</div>
            <div className="stat-label">Disponibilite</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">100%</div>
            <div className="stat-label">Forme a votre image</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">30+</div>
            <div className="stat-label">Langues</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">0</div>
            <div className="stat-label">Jour de conge</div>
          </div>
        </div>
      </section>

      {/* Section Metiers IA */}
      <section className="section-metiers">
        <div className="section-inner">
          <div className="section-header">
            <div className="section-badge"><Briefcase size={16} /> Metiers IA</div>
            <h2>Un employe IA pour <span className="text-gradient">chaque poste</span></h2>
            <p>Bornii s'adapte a tous les metiers de votre entreprise. Decouvrez tout ce que votre employe IA peut faire.</p>
          </div>

          {/* Carrousel metiers avec photos */}
          <div
            className="carousel-container"
            ref={carouselRef}
            onMouseEnter={pauseAutoPlay}
            onMouseLeave={resumeAutoPlay}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="carousel-track" style={{ transform: `translateX(-${carouselIndex * 100}%)` }}>
              {metiers.map((metier, index) => (
                <div key={index} className="carousel-slide">
                  <div className="carousel-card">
                    <div className="carousel-image">
                      <img src={metier.image} alt={metier.title} loading="lazy" />
                      <div className="carousel-image-overlay"></div>
                    </div>
                    <div className="carousel-card-content">
                      <div className="carousel-card-icon" style={{ background: `${metier.color}20`, color: metier.color }}>
                        <metier.icon size={22} />
                      </div>
                      <div className="carousel-card-title">{metier.title}</div>
                      <div className="carousel-card-desc">{metier.desc}</div>
                      <div className="carousel-card-bar" style={{ background: metier.color }}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation bar: arrows + dots */}
            <div className="carousel-nav">
              <button className="carousel-arrow" onClick={prevSlide} aria-label="Precedent">
                <ChevronLeft size={20} />
              </button>
              <div className="carousel-dots">
                {metiers.map((_, index) => (
                  <button
                    key={index}
                    className={`carousel-dot ${index === carouselIndex ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Slide ${index + 1}`}
                  />
                ))}
              </div>
              <button className="carousel-arrow" onClick={nextSlide} aria-label="Suivant">
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          <div className="secteurs-section">
            <h3>Adapte a tous les secteurs d'activite</h3>
            {/* Desktop: pills with icons */}
            <div className="secteurs-grid secteurs-desktop">
              {secteurs.map((s, index) => (
                <div key={index} className="secteur-pill">
                  <s.icon size={16} />
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
            {/* Mobile: simple text pills */}
            <div className="secteurs-grid secteurs-mobile">
              {secteurs.map((s, index) => (
                <div key={index} className="secteur-pill">
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section Employe IA */}
      <section className="section-employe" ref={employeSectionRef}>
        <div className="section-inner">
          <div className="section-header">
            <div className="section-badge"><Bot size={16} /> Employe IA</div>
            <h2>Bornii, <span className="text-gradient">l'Employe IA</span></h2>
            <p>Un collaborateur virtuel forme a votre image, qui connait votre entreprise sur le bout des doigts.</p>
          </div>

          <div className="avantages-grid">
            {avantagesEmployeIA.map((item, index) => (
              <div key={index} className="avantage-card">
                <div className="avantage-icon"><item.icon size={24} /></div>
                <div className="avantage-content">
                  <div className="avantage-title">{item.title}</div>
                  <div className="avantage-desc">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Borne IA */}
      <section className="section-borne">
        <div className="section-inner">
          <div className="section-header">
            <div className="section-badge"><Monitor size={16} /> Borne IA</div>
            <h2>Bornii, <span className="text-gradient">la Borne IA</span></h2>
            <p>Une borne physique intelligente qui remplace un employe dans votre etablissement.</p>
          </div>

          <div className="borne-layout">
            <div className="borne-features">
              {avantagesBorneIA.map((item, index) => (
                <div key={index} className="borne-feature-card">
                  <div className="borne-feature-icon"><item.icon size={28} /></div>
                  <div>
                    <div className="borne-feature-title">{item.title}</div>
                    <div className="borne-feature-desc">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="use-cases">
              <h3>Cas d'usage</h3>
              <div className="use-cases-grid">
                {useCases.map((uc, index) => (
                  <div key={index} className="use-case-pill">
                    <uc.icon size={16} />
                    <span>{uc.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Formulaire */}
      <section className="section-form" id="bornii-form">
        <div className="section-inner">
          <div className="form-layout">
            <div className="form-left">
              <h2>Pret a transformer <span className="text-gradient">votre entreprise ?</span></h2>
              <p>Remplissez le formulaire et un expert Bornii vous contactera pour une demonstration personnalisee gratuite.</p>

              <div className="form-guarantees">
                <div className="guarantee-item">
                  <CheckCircle size={18} />
                  <span>Demonstration gratuite et sans engagement</span>
                </div>
                <div className="guarantee-item">
                  <CheckCircle size={18} />
                  <span>Reponse sous 24h</span>
                </div>
                <div className="guarantee-item">
                  <CheckCircle size={18} />
                  <span>Installation et formation incluses</span>
                </div>
                <div className="guarantee-item">
                  <CheckCircle size={18} />
                  <span>Support technique illimite</span>
                </div>
              </div>

              <div className="contact-buttons">
                <a href="https://wa.me/33743678501?text=Bonjour%2C%20je%20souhaite%20en%20savoir%20plus%20sur%20Bornii." target="_blank" rel="noopener noreferrer" className="whatsapp-btn">
                  <MessageSquare size={14} /> Discuter sur WhatsApp
                </a>
                <a href="tel:+33743678501" className="phone-btn">
                  <Phone size={14} /> Appelez-nous
                </a>
              </div>
            </div>

            <div className="form-right" id="bornii-form-card">
              <div className="form-card">
                <div className="form-header">
                  <h2>Demander une demo</h2>
                  <p>Remplissez le formulaire ci-dessous</p>
                </div>

                {error && <div className="error-msg">{error}</div>}

                <form onSubmit={handleSubmit}>
                  <div className="input-group">
                    <label>Nom complet <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      onFocus={() => setFocusedField('nom')}
                      onBlur={() => setFocusedField(null)}
                      required
                      placeholder="Jean Dupont"
                      className={focusedField === 'nom' ? 'focused' : ''}
                    />
                  </div>

                  <div className="input-group">
                    <label>Email professionnel <span className="required">*</span></label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      required
                      placeholder="jean@entreprise.fr"
                      className={focusedField === 'email' ? 'focused' : ''}
                    />
                  </div>

                  <div className="input-row">
                    <div className="input-group">
                      <label>Telephone <span className="required">*</span></label>
                      <input
                        type="tel"
                        value={formData.telephone}
                        onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                        onFocus={() => setFocusedField('telephone')}
                        onBlur={() => setFocusedField(null)}
                        required
                        placeholder="06 12 34 56 78"
                        className={focusedField === 'telephone' ? 'focused' : ''}
                      />
                    </div>

                    <div className="input-group">
                      <label>Entreprise <span className="required">*</span></label>
                      <input
                        type="text"
                        value={formData.entreprise}
                        onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })}
                        onFocus={() => setFocusedField('entreprise')}
                        onBlur={() => setFocusedField(null)}
                        required
                        placeholder="Votre societe"
                        className={focusedField === 'entreprise' ? 'focused' : ''}
                      />
                    </div>
                  </div>

                  <div className="input-group">
                    <label>Votre besoin (optionnel)</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Decrivez votre projet ou votre besoin..."
                      rows={3}
                      className={focusedField === 'message' ? 'focused' : ''}
                    />
                  </div>

                  <button type="submit" className="submit-btn" disabled={loading}>
                    {loading ? 'Envoi en cours...' : 'Demander ma demo gratuite'}
                  </button>

                  <p className="disclaimer">
                    En soumettant ce formulaire, vous acceptez d'etre contacte par notre equipe.
                    Vos donnees sont protegees.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bornii-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <div className="logo">
              <div className="logo-icon"><Bot size={18} /></div>
              <span className="logo-text">Bornii</span>
            </div>
            <p>L'intelligence artificielle au service de votre entreprise.</p>
          </div>
          <div className="footer-links">
            <a href="/">Accueil E-Shops PRO</a>
            <a href="/audit-gratuit">Audit Gratuit</a>
            <a href="/offres">Nos Offres</a>
          </div>
          <div className="footer-legal">
            <p>Bornii est un produit de E-Shops PRO - E-Shops Connect</p>
            <p>SIRET 990 838 369 00017 - 44 Boulevard Davout, 75020 Paris</p>
          </div>
        </div>
      </footer>

      {/* Chatbot Widget */}
      {showChatbot && (
        <div className="chatbot-widget">
          <div className="chatbot-header">
            <div className="chatbot-header-left">
              <div className="chatbot-avatar"><Bot size={20} /></div>
              <div>
                <div className="chatbot-name">Bornii</div>
                <div className="chatbot-status">En ligne</div>
              </div>
            </div>
            <button className="chatbot-close" onClick={closeChatbot}><X size={18} /></button>
          </div>

          <div className="chatbot-messages" ref={chatMessagesRef}>
            {chatMessages.map((msg, index) => (
              <div key={index} className={`chatbot-msg chatbot-msg-${msg.type}`}>
                {msg.type === 'bot' && (
                  <div className="chatbot-msg-avatar"><Bot size={14} /></div>
                )}
                <div className={`chatbot-msg-bubble chatbot-bubble-${msg.type}`}>
                  {msg.text}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="chatbot-msg chatbot-msg-bot">
                <div className="chatbot-msg-avatar"><Bot size={14} /></div>
                <div className="chatbot-msg-bubble chatbot-bubble-bot chatbot-typing">
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                </div>
              </div>
            )}

            {!isTyping && chatMessages.length > 0 && chatMessages[chatMessages.length - 1].options && (
              <div className="chatbot-options">
                {chatMessages[chatMessages.length - 1].options.map((opt, idx) => (
                  <button key={idx} className="chatbot-option-btn" onClick={() => handleChatOption(opt)}>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chatbot Modal Popup (center screen) */}
      {showChatbotModal && (
        <div className="chatbot-modal-overlay">
          <div className="chatbot-modal">
            <div className="chatbot-modal-header">
              <div className="chatbot-modal-header-left">
                <div className="chatbot-modal-avatar"><Bot size={28} /></div>
                <div>
                  <div className="chatbot-modal-title">Bornii - Assistant IA</div>
                  <div className="chatbot-modal-subtitle">Discutons de votre projet</div>
                </div>
              </div>
              <button className="chatbot-modal-close" onClick={closeChatbotModal}><X size={20} /></button>
            </div>

            <div className="chatbot-modal-body" ref={chatModalMessagesRef}>
              {modalMessages.map((msg, index) => (
                <div key={index} className={`chatbot-msg chatbot-msg-${msg.type}`}>
                  {msg.type === 'bot' && (
                    <div className="chatbot-msg-avatar"><Bot size={16} /></div>
                  )}
                  <div className={`chatbot-msg-bubble chatbot-bubble-${msg.type}`}>
                    {msg.text}
                  </div>
                </div>
              ))}

              {isModalTyping && (
                <div className="chatbot-msg chatbot-msg-bot">
                  <div className="chatbot-msg-avatar"><Bot size={16} /></div>
                  <div className="chatbot-msg-bubble chatbot-bubble-bot chatbot-typing">
                    <span className="typing-dot"></span>
                    <span className="typing-dot"></span>
                    <span className="typing-dot"></span>
                  </div>
                </div>
              )}

              {!isModalTyping && modalMessages.length > 0 && modalMessages[modalMessages.length - 1].options && (
                <div className="chatbot-modal-options">
                  {modalMessages[modalMessages.length - 1].options.map((opt, idx) => (
                    <button key={idx} className="chatbot-modal-option-btn" onClick={() => handleModalOption(opt)}>
                      <ArrowRight size={14} />
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="chatbot-modal-footer">
              <div className="chatbot-modal-footer-icon"><Zap size={14} /></div>
              <span>Propulse par l'IA Bornii - Reponses instantanees</span>
            </div>
          </div>
        </div>
      )}

      {/* Chatbot FAB (always visible when chat is closed) */}
      {!showChatbot && (
        <button className="chatbot-fab" onClick={reopenChatbot}>
          <Bot size={24} />
        </button>
      )}
    </div>
  );
}

function getStyles() {
  return `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

    .bornii-page {
      min-height: 100vh;
      background: #0a0a0a;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      position: relative;
      overflow-x: hidden;
      color: #fff;
      -webkit-overflow-scrolling: touch;
    }

    .bg-gradient {
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 100%;
      min-height: 100vh;
      background:
        radial-gradient(ellipse at 20% 20%, rgba(102,126,234,0.15) 0%, transparent 50%),
        radial-gradient(ellipse at 80% 80%, rgba(118,75,162,0.12) 0%, transparent 50%),
        radial-gradient(ellipse at 50% 50%, rgba(67,233,123,0.06) 0%, transparent 70%);
      pointer-events: none;
      z-index: 0;
    }

    .bg-grid {
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 100%;
      min-height: 100vh;
      background-image:
        linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
      background-size: 50px 50px;
      pointer-events: none;
      z-index: 0;
    }

    .floating-elements {
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 100%;
      min-height: 100vh;
      pointer-events: none;
      z-index: 1;
      overflow: hidden;
    }

    .floating-circle {
      position: absolute;
      border-radius: 50%;
      animation: float 20s ease-in-out infinite;
      will-change: transform;
    }

    .c1 {
      width: 350px; height: 350px;
      background: radial-gradient(circle, rgba(102,126,234,0.12) 0%, transparent 70%);
      top: 10%; left: -5%;
      animation-delay: 0s;
    }

    .c2 {
      width: 400px; height: 400px;
      background: radial-gradient(circle, rgba(118,75,162,0.1) 0%, transparent 70%);
      top: 50%; right: -10%;
      animation-delay: -7s;
    }

    .c3 {
      width: 300px; height: 300px;
      background: radial-gradient(circle, rgba(67,233,123,0.08) 0%, transparent 70%);
      bottom: 10%; left: 30%;
      animation-delay: -14s;
    }

    @keyframes float {
      0%, 100% { transform: translate(0, 0) scale(1); }
      25% { transform: translate(30px, -30px) scale(1.05); }
      50% { transform: translate(-20px, 20px) scale(0.95); }
      75% { transform: translate(20px, 30px) scale(1.02); }
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(1.2); }
    }

    @keyframes gradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    /* Header */
    .bornii-header {
      position: relative;
      z-index: 10;
      padding: 25px 40px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid rgba(255,255,255,0.06);
    }

    .logo {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .logo-icon {
      width: 42px; height: 42px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      box-shadow: 0 4px 15px rgba(102,126,234,0.4);
    }

    .logo-text {
      font-size: 22px;
      font-weight: 800;
      background: linear-gradient(135deg, #fff 0%, #c0c0c0 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .logo-sub {
      font-size: 12px;
      color: rgba(255,255,255,0.4);
      font-weight: 500;
    }

    .date-text {
      color: rgba(255,255,255,0.4);
      font-size: 13px;
      text-transform: capitalize;
    }

    /* Hero Section */
    .hero-section {
      position: relative;
      z-index: 10;
      padding: 60px 40px 60px;
      max-width: 1200px;
      margin: 0 auto;
    }

    .hero-layout {
      display: flex;
      align-items: center;
      gap: 60px;
    }

    .badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 18px;
      background: rgba(67,233,123,0.1);
      border: 1px solid rgba(67,233,123,0.3);
      border-radius: 50px;
      margin-bottom: 30px;
      color: #43e97b;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .badge-dot {
      width: 8px; height: 8px;
      background: #43e97b;
      border-radius: 50%;
      animation: pulse 2s infinite;
    }

    .hero-title {
      font-size: 58px;
      font-weight: 900;
      line-height: 1.1;
      margin-bottom: 25px;
      letter-spacing: -2.5px;
    }

    .title-white { color: #fff; }

    .title-gradient {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #43e97b 100%);
      background-size: 200% 200%;
      animation: gradientShift 4s ease infinite;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .text-gradient {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #43e97b 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .hero-content {
      flex: 1 1 55%;
      min-width: 0;
    }

    .hero-subtitle {
      font-size: 18px;
      line-height: 1.7;
      color: rgba(255,255,255,0.6);
      margin-bottom: 35px;
      max-width: 520px;
    }

    /* Hero Image */
    .hero-image {
      flex: 0 0 380px;
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .hero-image img {
      width: 100%;
      max-width: 360px;
      height: auto;
      border-radius: 20px;
      position: relative;
      z-index: 2;
      box-shadow: 0 30px 60px rgba(0,0,0,0.5), 0 0 80px rgba(102,126,234,0.15);
      animation: heroFloat 6s ease-in-out infinite;
    }

    .hero-image-glow {
      position: absolute;
      width: 300px;
      height: 300px;
      background: radial-gradient(circle, rgba(102,126,234,0.25) 0%, rgba(118,75,162,0.15) 40%, transparent 70%);
      border-radius: 50%;
      z-index: 1;
      filter: blur(30px);
      animation: glowPulse 4s ease-in-out infinite;
    }

    .hero-image-badge {
      position: absolute;
      bottom: 20px;
      right: -10px;
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 10px 16px;
      background: rgba(10,10,10,0.8);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(67,233,123,0.3);
      border-radius: 50px;
      font-size: 12px;
      font-weight: 600;
      color: #43e97b;
      z-index: 3;
      box-shadow: 0 8px 25px rgba(0,0,0,0.3);
      animation: heroFloat 6s ease-in-out infinite;
      animation-delay: -3s;
    }

    /* Image mobile (entre titre et sous-titre) - cachee par defaut */
    .hero-image-mobile {
      display: none;
    }

    @keyframes heroFloat {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-12px); }
    }

    @keyframes glowPulse {
      0%, 100% { opacity: 0.7; transform: scale(1); }
      50% { opacity: 1; transform: scale(1.1); }
    }

    .hero-price {
      display: flex;
      align-items: baseline;
      gap: 6px;
      margin-bottom: 30px;
    }

    .price-label {
      font-size: 14px;
      color: rgba(255,255,255,0.5);
      font-weight: 500;
    }

    .price-amount {
      font-size: 56px;
      font-weight: 900;
      background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .price-currency {
      font-size: 28px;
    }

    .price-period {
      font-size: 18px;
      color: rgba(255,255,255,0.5);
      font-weight: 500;
    }

    .cta-btn {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 18px 36px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      border-radius: 14px;
      font-size: 16px;
      font-weight: 700;
      color: #fff;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 20px rgba(102,126,234,0.4);
      font-family: inherit;
    }

    .cta-btn:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 30px rgba(102,126,234,0.5);
    }

    .hero-stats {
      display: flex;
      justify-content: space-between;
      gap: 30px;
      margin-top: 40px;
      padding-top: 30px;
      border-top: 1px solid rgba(255,255,255,0.08);
      max-width: 1100px;
      margin-left: auto;
      margin-right: auto;
    }

    .stat-item { text-align: center; }

    .stat-number {
      font-size: 32px;
      font-weight: 800;
      background: linear-gradient(135deg, #fff 0%, #667eea 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .stat-label {
      font-size: 12px;
      color: rgba(255,255,255,0.4);
      margin-top: 4px;
      font-weight: 500;
    }

    /* Sections Common */
    .section-employe,
    .section-borne,
    .section-prix,
    .section-form {
      position: relative;
      z-index: 10;
      padding: 80px 40px;
    }

    .section-inner {
      max-width: 1100px;
      margin: 0 auto;
    }

    .section-header {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      margin-bottom: 50px;
    }

    .section-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: rgba(102,126,234,0.1);
      border: 1px solid rgba(102,126,234,0.25);
      border-radius: 50px;
      color: #667eea;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 20px;
    }

    .section-header h2 {
      font-size: 40px;
      font-weight: 800;
      letter-spacing: -1.5px;
      margin-bottom: 15px;
      width: 100%;
      color: #fff;
    }

    .section-header p {
      font-size: 17px;
      color: rgba(255,255,255,0.6);
      max-width: 550px;
      line-height: 1.7;
      width: 100%;
    }

    /* Avantages Grid */
    .avantages-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
    }

    .avantage-card {
      padding: 28px;
      background: rgba(255,255,255,0.02);
      border-radius: 18px;
      border: 1px solid rgba(255,255,255,0.06);
      transition: all 0.3s ease;
    }

    .avantage-card:hover {
      background: rgba(255,255,255,0.04);
      border-color: rgba(102,126,234,0.2);
      transform: translateY(-4px);
    }

    .avantage-icon {
      width: 48px; height: 48px;
      background: linear-gradient(135deg, rgba(102,126,234,0.15), rgba(118,75,162,0.15));
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;
      color: #667eea;
    }

    .avantage-title {
      font-size: 16px;
      font-weight: 700;
      color: #fff;
      margin-bottom: 8px;
    }

    .avantage-desc {
      font-size: 14px;
      color: rgba(255,255,255,0.5);
      line-height: 1.5;
    }

    /* Section Metiers */
    .section-metiers {
      position: relative;
      z-index: 10;
      padding: 80px 40px;
      border-top: 1px solid rgba(255,255,255,0.04);
    }

    /* Mobile version hidden on desktop */
    .secteurs-mobile {
      display: none !important;
    }

    /* === CAROUSEL === */
    .carousel-container {
      position: relative;
      overflow: hidden;
      border-radius: 20px;
      margin-bottom: 50px;
    }

    .carousel-track {
      display: flex;
      transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .carousel-slide {
      min-width: 100%;
      padding: 0 4px;
      box-sizing: border-box;
    }

    .carousel-card {
      position: relative;
      border-radius: 20px;
      overflow: hidden;
      border: 1px solid rgba(255,255,255,0.08);
      background: rgba(255,255,255,0.02);
      height: 380px;
    }

    .carousel-image {
      position: absolute;
      inset: 0;
      z-index: 1;
    }

    .carousel-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .carousel-image-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(to top, rgba(8,8,16,0.95) 0%, rgba(8,8,16,0.7) 40%, rgba(8,8,16,0.2) 100%);
      z-index: 2;
    }

    .carousel-card-content {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 30px;
      z-index: 3;
    }

    .carousel-card-icon {
      width: 48px;
      height: 48px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255,255,255,0.1);
    }

    .carousel-card-title {
      font-size: 24px;
      font-weight: 800;
      color: #fff;
      margin-bottom: 10px;
      letter-spacing: -0.5px;
    }

    .carousel-card-desc {
      font-size: 15px;
      color: rgba(255,255,255,0.7);
      line-height: 1.6;
      max-width: 500px;
    }

    .carousel-card-bar {
      width: 50px;
      height: 3px;
      border-radius: 3px;
      margin-top: 16px;
    }

    /* Carousel nav bar (arrows + dots) */
    .carousel-nav {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 16px;
      margin-top: 24px;
    }

    .carousel-arrow {
      background: rgba(255,255,255,0.06);
      border: 1px solid rgba(255,255,255,0.12);
      color: #fff;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      flex-shrink: 0;
    }

    .carousel-arrow:hover {
      background: rgba(255,255,255,0.12);
      border-color: rgba(255,255,255,0.25);
    }

    .carousel-dots {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      justify-content: center;
    }

    .carousel-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      border: none;
      background: rgba(255,255,255,0.15);
      cursor: pointer;
      transition: all 0.3s ease;
      padding: 0;
    }

    .carousel-dot.active {
      background: #667eea;
      transform: scale(1.3);
      box-shadow: 0 0 12px rgba(102,126,234,0.5);
    }

    .carousel-dot:hover:not(.active) {
      background: rgba(255,255,255,0.3);
    }

    .secteurs-section {
      text-align: center;
      padding-top: 40px;
      border-top: 1px solid rgba(255,255,255,0.06);
    }

    .secteurs-section h3 {
      font-size: 22px;
      font-weight: 700;
      color: #fff;
      margin-bottom: 25px;
    }

    .secteurs-grid {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 10px;
    }

    .secteur-pill {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 18px;
      background: rgba(255,255,255,0.03);
      border: 1px solid rgba(255,255,255,0.08);
      border-radius: 50px;
      font-size: 13px;
      color: rgba(255,255,255,0.75);
      transition: all 0.3s ease;
    }

    .secteur-pill:hover {
      background: rgba(102,126,234,0.1);
      border-color: rgba(102,126,234,0.3);
      transform: translateY(-2px);
    }

    /* Section Borne */
    .section-borne {
      border-top: 1px solid rgba(255,255,255,0.04);
    }

    .borne-layout {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 40px;
      align-items: start;
    }

    .borne-feature-card {
      display: flex;
      align-items: flex-start;
      gap: 18px;
      padding: 24px;
      background: rgba(255,255,255,0.02);
      border-radius: 16px;
      border: 1px solid rgba(255,255,255,0.06);
      margin-bottom: 16px;
      transition: all 0.3s ease;
    }

    .borne-feature-card:hover {
      background: rgba(255,255,255,0.04);
      border-color: rgba(67,233,123,0.2);
      transform: translateX(5px);
    }

    .borne-feature-icon {
      width: 52px; height: 52px;
      background: linear-gradient(135deg, rgba(67,233,123,0.15), rgba(56,249,215,0.15));
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #43e97b;
      flex-shrink: 0;
    }

    .borne-feature-title {
      font-size: 16px;
      font-weight: 700;
      color: #fff;
      margin-bottom: 6px;
    }

    .borne-feature-desc {
      font-size: 14px;
      color: rgba(255,255,255,0.5);
      line-height: 1.5;
    }

    .use-cases h3 {
      font-size: 20px;
      font-weight: 700;
      margin-bottom: 20px;
      color: #fff;
    }

    .use-cases-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    }

    .use-case-pill {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 14px 18px;
      background: rgba(255,255,255,0.03);
      border: 1px solid rgba(255,255,255,0.08);
      border-radius: 12px;
      font-size: 14px;
      color: rgba(255,255,255,0.8);
      transition: all 0.3s ease;
    }

    .use-case-pill:hover {
      background: rgba(67,233,123,0.08);
      border-color: rgba(67,233,123,0.25);
      transform: translateY(-2px);
    }

    /* Section Prix */
    .section-prix {
      border-top: 1px solid rgba(255,255,255,0.04);
    }

    .prix-comparison {
      display: flex;
      align-items: stretch;
      justify-content: center;
      gap: 30px;
    }

    .prix-card {
      flex: 1;
      max-width: 380px;
      padding: 35px;
      border-radius: 24px;
      position: relative;
    }

    .prix-employe {
      background: rgba(255,255,255,0.02);
      border: 1px solid rgba(255,255,255,0.08);
    }

    .prix-bornii {
      background: rgba(67,233,123,0.04);
      border: 2px solid rgba(67,233,123,0.25);
      box-shadow: 0 0 40px rgba(67,233,123,0.08);
    }

    .prix-card-badge {
      position: absolute;
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 16px;
      background: linear-gradient(135deg, #43e97b, #38f9d7);
      border-radius: 50px;
      font-size: 12px;
      font-weight: 700;
      color: #0a0a0a;
      white-space: nowrap;
    }

    .prix-card-label {
      font-size: 14px;
      color: rgba(255,255,255,0.5);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 12px;
    }

    .prix-card-amount {
      font-size: 48px;
      font-weight: 900;
      color: #fff;
      margin-bottom: 25px;
    }

    .prix-card-amount span {
      font-size: 16px;
      font-weight: 500;
      color: rgba(255,255,255,0.5);
    }

    .prix-bornii .prix-card-amount {
      background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .prix-list {
      list-style: none;
      padding: 0;
      margin: 0 0 25px;
    }

    .prix-list li {
      padding: 10px 0;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 10px;
      border-bottom: 1px solid rgba(255,255,255,0.04);
    }

    .prix-con::before {
      content: '';
      width: 8px; height: 8px;
      background: #ef4444;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .prix-con {
      color: rgba(255,255,255,0.5);
    }

    .prix-pro::before {
      content: '';
      width: 8px; height: 8px;
      background: #43e97b;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .prix-pro {
      color: rgba(255,255,255,0.8);
    }

    .prix-vs {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 28px;
      font-weight: 900;
      color: rgba(255,255,255,0.15);
      padding: 0 10px;
    }

    .prix-economie {
      background: rgba(67,233,123,0.1);
      border: 1px solid rgba(67,233,123,0.2);
      border-radius: 12px;
      padding: 16px;
      text-align: center;
      margin-bottom: 20px;
    }

    .prix-economie span {
      display: block;
      font-size: 12px;
      color: rgba(255,255,255,0.5);
      margin-bottom: 4px;
    }

    .prix-economie strong {
      font-size: 22px;
      font-weight: 800;
      color: #43e97b;
    }

    /* Section Form */
    .section-form {
      border-top: 1px solid rgba(255,255,255,0.04);
    }

    .form-layout {
      display: flex;
      gap: 60px;
      align-items: flex-start;
    }

    .form-left {
      flex: 1;
    }

    .form-left h2 {
      font-size: 36px;
      font-weight: 800;
      letter-spacing: -1px;
      margin-bottom: 15px;
      line-height: 1.2;
    }

    .form-left > p {
      font-size: 16px;
      color: rgba(255,255,255,0.6);
      line-height: 1.7;
      margin-bottom: 30px;
    }

    .form-guarantees {
      margin-bottom: 30px;
    }

    .guarantee-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 0;
      color: rgba(255,255,255,0.7);
      font-size: 15px;
    }

    .guarantee-item svg {
      color: #43e97b;
      flex-shrink: 0;
    }

    .form-right {
      flex: 0 0 420px;
    }

    /* Form Card */
    .form-card {
      background: rgba(255,255,255,0.03);
      backdrop-filter: blur(20px);
      border-radius: 24px;
      border: 1px solid rgba(255,255,255,0.08);
      padding: 35px;
      box-shadow: 0 25px 50px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05);
    }

    .form-header {
      margin-bottom: 25px;
      text-align: center;
    }

    .form-header h2 {
      font-size: 24px;
      font-weight: 700;
      color: #fff;
      margin-bottom: 8px;
    }

    .form-header p {
      font-size: 14px;
      color: rgba(255,255,255,0.5);
    }

    .input-group {
      margin-bottom: 18px;
    }

    .input-row {
      display: flex;
      gap: 12px;
    }

    .input-row .input-group {
      flex: 1;
    }

    .input-group label {
      display: block;
      font-size: 12px;
      font-weight: 600;
      color: rgba(255,255,255,0.7);
      margin-bottom: 8px;
    }

    .required {
      color: #ec4899;
    }

    .input-group input,
    .input-group textarea {
      width: 100%;
      padding: 14px 16px;
      background: rgba(255,255,255,0.03);
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 10px;
      font-size: 14px;
      color: #fff;
      outline: none;
      transition: all 0.3s ease;
      box-sizing: border-box;
      font-family: inherit;
    }

    .input-group input::placeholder,
    .input-group textarea::placeholder {
      color: rgba(255,255,255,0.3);
    }

    .input-group input.focused,
    .input-group textarea.focused {
      border-color: #667eea;
      background: rgba(102,126,234,0.05);
      box-shadow: 0 0 0 3px rgba(102,126,234,0.1);
    }

    .input-group textarea {
      resize: vertical;
      min-height: 80px;
    }

    .submit-btn {
      width: 100%;
      padding: 16px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      border-radius: 12px;
      font-size: 15px;
      font-weight: 700;
      color: #fff;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 15px rgba(102,126,234,0.4);
      margin-top: 5px;
      font-family: inherit;
    }

    .submit-btn:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(102,126,234,0.5);
    }

    .submit-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .disclaimer {
      text-align: center;
      font-size: 11px;
      color: rgba(255,255,255,0.35);
      margin-top: 15px;
      line-height: 1.5;
    }

    .contact-buttons {
      display: flex;
      gap: 10px;
    }

    .whatsapp-btn, .phone-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 12px 20px;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 600;
      text-decoration: none;
      transition: all 0.3s ease;
    }

    .whatsapp-btn {
      background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
      color: white;
      box-shadow: 0 4px 15px rgba(37,211,102,0.3);
    }

    .whatsapp-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(37,211,102,0.4);
    }

    .phone-btn {
      background: rgba(255,255,255,0.05);
      border: 1px solid rgba(255,255,255,0.2);
      color: white;
    }

    .phone-btn:hover {
      background: rgba(255,255,255,0.1);
      transform: translateY(-2px);
    }

    .error-msg {
      background: rgba(239,68,68,0.1);
      border: 1px solid rgba(239,68,68,0.3);
      color: #ef4444;
      padding: 12px 15px;
      border-radius: 10px;
      margin-bottom: 18px;
      font-size: 13px;
    }

    /* Success */
    .success-container {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;
      position: relative;
      z-index: 10;
    }

    .success-card {
      background: rgba(255,255,255,0.03);
      backdrop-filter: blur(20px);
      border-radius: 24px;
      border: 1px solid rgba(255,255,255,0.08);
      padding: 50px;
      text-align: center;
      max-width: 450px;
    }

    .success-icon {
      width: 80px; height: 80px;
      background: linear-gradient(135deg, #43e97b, #38f9d7);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 25px;
      color: #0a0a0a;
      box-shadow: 0 10px 30px rgba(67,233,123,0.3);
    }

    .success-card h1 {
      font-size: 28px;
      color: #fff;
      margin-bottom: 15px;
    }

    .success-card p {
      color: rgba(255,255,255,0.6);
      line-height: 1.7;
      margin-bottom: 25px;
    }

    .success-info {
      background: rgba(67,233,123,0.1);
      border: 1px solid rgba(67,233,123,0.2);
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 25px;
    }

    .success-info strong {
      display: block;
      color: #43e97b;
      margin-bottom: 8px;
    }

    .success-info span {
      color: rgba(255,255,255,0.6);
      font-size: 14px;
    }

    .back-btn {
      display: inline-block;
      padding: 14px 28px;
      background: rgba(255,255,255,0.05);
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 10px;
      color: #fff;
      text-decoration: none;
      font-weight: 600;
      transition: all 0.3s ease;
    }

    .back-btn:hover {
      background: rgba(255,255,255,0.1);
    }

    /* Footer */
    .bornii-footer {
      position: relative;
      z-index: 10;
      border-top: 1px solid rgba(255,255,255,0.06);
      padding: 40px;
    }

    .footer-inner {
      max-width: 1100px;
      margin: 0 auto;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 20px;
    }

    .footer-brand p {
      font-size: 13px;
      color: rgba(255,255,255,0.4);
      margin-top: 8px;
    }

    .footer-links {
      display: flex;
      gap: 25px;
    }

    .footer-links a {
      font-size: 13px;
      color: rgba(255,255,255,0.5);
      text-decoration: none;
      transition: color 0.3s;
    }

    .footer-links a:hover {
      color: #667eea;
    }

    .footer-legal {
      text-align: right;
    }

    .footer-legal p {
      font-size: 11px;
      color: rgba(255,255,255,0.3);
      line-height: 1.6;
    }

    /* ============================================
       CHATBOT WIDGET
       ============================================ */

    .chatbot-widget {
      position: fixed;
      bottom: 24px;
      right: 24px;
      width: 380px;
      max-height: 520px;
      background: #111113;
      border: 1px solid rgba(102,126,234,0.3);
      border-radius: 20px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(102,126,234,0.15);
      display: flex;
      flex-direction: column;
      z-index: 9999;
      animation: chatbot-slide-up 0.4s ease-out;
      overflow: hidden;
    }

    @keyframes chatbot-slide-up {
      from { opacity: 0; transform: translateY(30px) scale(0.95); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }

    .chatbot-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 18px;
      background: linear-gradient(135deg, rgba(102,126,234,0.2) 0%, rgba(118,75,162,0.15) 100%);
      border-bottom: 1px solid rgba(255,255,255,0.08);
    }

    .chatbot-header-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .chatbot-avatar {
      width: 40px;
      height: 40px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      box-shadow: 0 4px 12px rgba(102,126,234,0.4);
    }

    .chatbot-name {
      font-size: 15px;
      font-weight: 700;
      color: #fff;
    }

    .chatbot-status {
      font-size: 11px;
      color: #43e97b;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 5px;
    }

    .chatbot-status::before {
      content: '';
      width: 6px;
      height: 6px;
      background: #43e97b;
      border-radius: 50%;
      animation: pulse 2s infinite;
    }

    .chatbot-close {
      background: rgba(255,255,255,0.08);
      border: none;
      color: rgba(255,255,255,0.6);
      cursor: pointer;
      width: 32px;
      height: 32px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .chatbot-close:hover {
      background: rgba(255,255,255,0.15);
      color: #fff;
    }

    .chatbot-messages {
      flex: 1;
      overflow-y: auto;
      padding: 18px 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      max-height: 360px;
      min-height: 200px;
      scrollbar-width: thin;
      scrollbar-color: rgba(102,126,234,0.3) transparent;
    }

    .chatbot-messages::-webkit-scrollbar {
      width: 4px;
    }

    .chatbot-messages::-webkit-scrollbar-track {
      background: transparent;
    }

    .chatbot-messages::-webkit-scrollbar-thumb {
      background: rgba(102,126,234,0.3);
      border-radius: 4px;
    }

    .chatbot-msg {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      animation: chatbot-msg-in 0.3s ease-out;
    }

    @keyframes chatbot-msg-in {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .chatbot-msg-bot {
      justify-content: flex-start;
    }

    .chatbot-msg-user {
      justify-content: flex-end;
    }

    .chatbot-msg-avatar {
      width: 28px;
      height: 28px;
      min-width: 28px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      margin-top: 2px;
    }

    .chatbot-msg-bubble {
      padding: 10px 14px;
      border-radius: 14px;
      font-size: 13px;
      line-height: 1.5;
      max-width: 80%;
    }

    .chatbot-bubble-bot {
      background: rgba(255,255,255,0.07);
      color: rgba(255,255,255,0.9);
      border-bottom-left-radius: 4px;
    }

    .chatbot-bubble-user {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;
      border-bottom-right-radius: 4px;
    }

    .chatbot-typing {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 12px 18px;
    }

    .typing-dot {
      width: 7px;
      height: 7px;
      background: rgba(255,255,255,0.4);
      border-radius: 50%;
      animation: typing-bounce 1.4s ease-in-out infinite;
    }

    .typing-dot:nth-child(2) {
      animation-delay: 0.2s;
    }

    .typing-dot:nth-child(3) {
      animation-delay: 0.4s;
    }

    @keyframes typing-bounce {
      0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
      30% { transform: translateY(-6px); opacity: 1; }
    }

    .chatbot-options {
      display: flex;
      flex-direction: column;
      gap: 6px;
      margin-top: 4px;
      padding-left: 36px;
      animation: chatbot-msg-in 0.3s ease-out 0.1s both;
    }

    .chatbot-option-btn {
      background: rgba(102,126,234,0.1);
      border: 1px solid rgba(102,126,234,0.3);
      color: #a0b4ff;
      padding: 9px 14px;
      border-radius: 10px;
      font-size: 12.5px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      text-align: left;
      font-family: inherit;
    }

    .chatbot-option-btn:hover {
      background: rgba(102,126,234,0.2);
      border-color: rgba(102,126,234,0.5);
      color: #fff;
      transform: translateX(4px);
    }

    .chatbot-fab {
      position: fixed;
      bottom: 24px;
      right: 24px;
      width: 56px;
      height: 56px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      border-radius: 16px;
      color: #fff;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 8px 30px rgba(102,126,234,0.5);
      z-index: 9999;
      transition: all 0.3s;
      animation: chatbot-slide-up 0.3s ease-out;
    }

    .chatbot-fab:hover {
      transform: scale(1.08);
      box-shadow: 0 12px 40px rgba(102,126,234,0.6);
    }

    /* ============================================
       CHATBOT MODAL (CENTER POPUP)
       ============================================ */

    .chatbot-modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.75);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      animation: modal-overlay-in 0.3s ease-out;
    }

    @keyframes modal-overlay-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .chatbot-modal {
      width: 100%;
      max-width: 500px;
      max-height: 80vh;
      background: #111113;
      border: 1px solid rgba(102,126,234,0.35);
      border-radius: 24px;
      box-shadow:
        0 30px 80px rgba(0,0,0,0.7),
        0 0 60px rgba(102,126,234,0.2),
        inset 0 1px 0 rgba(255,255,255,0.06);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      animation: modal-pop-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    @keyframes modal-pop-in {
      from { opacity: 0; transform: scale(0.85) translateY(30px); }
      to { opacity: 1; transform: scale(1) translateY(0); }
    }

    .chatbot-modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 22px 24px;
      background: linear-gradient(135deg, rgba(102,126,234,0.25) 0%, rgba(118,75,162,0.2) 100%);
      border-bottom: 1px solid rgba(255,255,255,0.08);
    }

    .chatbot-modal-header-left {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    .chatbot-modal-avatar {
      width: 50px;
      height: 50px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      box-shadow: 0 6px 20px rgba(102,126,234,0.5);
    }

    .chatbot-modal-title {
      font-size: 18px;
      font-weight: 700;
      color: #fff;
    }

    .chatbot-modal-subtitle {
      font-size: 13px;
      color: #43e97b;
      font-weight: 500;
      margin-top: 2px;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .chatbot-modal-subtitle::before {
      content: '';
      width: 7px;
      height: 7px;
      background: #43e97b;
      border-radius: 50%;
      animation: pulse 2s infinite;
    }

    .chatbot-modal-close {
      background: rgba(255,255,255,0.08);
      border: none;
      color: rgba(255,255,255,0.6);
      cursor: pointer;
      width: 38px;
      height: 38px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .chatbot-modal-close:hover {
      background: rgba(255,255,255,0.15);
      color: #fff;
    }

    .chatbot-modal-body {
      flex: 1;
      overflow-y: auto;
      padding: 24px;
      display: flex;
      flex-direction: column;
      gap: 14px;
      max-height: 50vh;
      min-height: 250px;
      scrollbar-width: thin;
      scrollbar-color: rgba(102,126,234,0.3) transparent;
    }

    .chatbot-modal-body::-webkit-scrollbar {
      width: 4px;
    }

    .chatbot-modal-body::-webkit-scrollbar-track {
      background: transparent;
    }

    .chatbot-modal-body::-webkit-scrollbar-thumb {
      background: rgba(102,126,234,0.3);
      border-radius: 4px;
    }

    .chatbot-modal-body .chatbot-msg-bubble {
      font-size: 14px;
      padding: 12px 16px;
      max-width: 85%;
    }

    .chatbot-modal-body .chatbot-msg-avatar {
      width: 32px;
      height: 32px;
      min-width: 32px;
    }

    .chatbot-modal-options {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: 6px;
      padding-left: 42px;
      animation: chatbot-msg-in 0.3s ease-out 0.1s both;
    }

    .chatbot-modal-option-btn {
      background: rgba(102,126,234,0.1);
      border: 1px solid rgba(102,126,234,0.3);
      color: #a0b4ff;
      padding: 12px 16px;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.25s;
      text-align: left;
      font-family: inherit;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .chatbot-modal-option-btn svg {
      opacity: 0;
      transform: translateX(-6px);
      transition: all 0.25s;
      flex-shrink: 0;
    }

    .chatbot-modal-option-btn:hover {
      background: linear-gradient(135deg, rgba(102,126,234,0.25) 0%, rgba(118,75,162,0.15) 100%);
      border-color: rgba(102,126,234,0.6);
      color: #fff;
      transform: translateX(4px);
    }

    .chatbot-modal-option-btn:hover svg {
      opacity: 1;
      transform: translateX(0);
    }

    .chatbot-modal-footer {
      padding: 14px 24px;
      border-top: 1px solid rgba(255,255,255,0.06);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 11px;
      color: rgba(255,255,255,0.3);
    }

    .chatbot-modal-footer-icon {
      color: #43e97b;
      display: flex;
    }

    /* ============================================
       MOBILE - Complete rewrite for performance
       ============================================ */

    /* Tablet */
    @media (max-width: 1024px) {
      .hero-section { padding: 40px 20px 30px; }
      .hero-layout { flex-direction: column; text-align: center; }
      .hero-image { margin-top: 10px; }
      .hero-image img { max-width: 260px; }
      .hero-image-badge { right: auto; }
      .hero-subtitle { max-width: 100%; margin: 0 auto 30px; }
      .hero-title { font-size: 42px; }
      .hero-price { justify-content: center; }
      .cta-btn { margin: 0 auto; }
      .avantages-grid { grid-template-columns: repeat(2, 1fr); }
      .carousel-card { height: 340px; }
      .section-metiers { padding: 50px 20px; }
      .borne-layout { grid-template-columns: 1fr; }
      .prix-comparison { flex-direction: column; align-items: center; }
      .prix-card { max-width: 100%; width: 100%; }
      .prix-vs { padding: 10px 0; }
      .form-layout { flex-direction: column; gap: 30px; }
      .form-left { text-align: center; }
      .form-right { flex: 1 1 auto; width: 100%; max-width: 100%; }
      .section-employe, .section-borne, .section-prix, .section-form { padding: 50px 20px; }
    }

    /* Mobile */
    @media (max-width: 768px) {
      /* === PERFORMANCE: Kill everything heavy === */
      * { -webkit-tap-highlight-color: transparent; }
      .floating-elements, .bg-grid, .hero-image-glow { display: none !important; }
      .bg-gradient { position: absolute; height: 500px; opacity: 0.7; }
      .secteurs-desktop { display: none !important; }
      .secteurs-mobile { display: flex !important; }

      /* Kill ALL transitions, animations, blur */
      .bornii-page *, .bornii-page *::before, .bornii-page *::after {
        transition: none !important;
        animation: none !important;
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
      }

      /* === HEADER === */
      .bornii-header { padding: 14px 20px; }
      .logo-icon { width: 36px; height: 36px; box-shadow: none; }
      .logo-text { font-size: 20px; }
      .date-text, .logo-sub { display: none; }

      /* === HERO === */
      .hero-section { padding: 32px 20px 24px; }
      .hero-layout { flex-direction: column; text-align: center; gap: 24px; }
      .badge { margin-bottom: 16px; padding: 7px 14px; font-size: 11px; }
      .hero-title { font-size: 30px; letter-spacing: -1.2px; margin-bottom: 0; }

      /* Cacher image desktop, afficher image mobile entre titre et sous-titre */
      .hero-image { display: none !important; }
      .hero-image-mobile {
        display: block;
        text-align: center;
        margin: 20px auto;
      }
      .hero-image-mobile img {
        max-width: 220px;
        width: 100%;
        height: auto;
        border-radius: 16px;
        box-shadow: 0 16px 40px rgba(0,0,0,0.4);
      }

      .hero-subtitle { font-size: 15px; line-height: 1.6; margin: 0 auto 24px; }
      .price-amount { font-size: 38px; }
      .price-currency { font-size: 20px; }
      .hero-price { justify-content: center; margin-bottom: 20px; gap: 5px; }
      .cta-btn { width: 100%; justify-content: center; padding: 16px 24px; font-size: 15px; border-radius: 12px; box-shadow: none; }

      /* Stats */
      .hero-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-top: 28px; padding-top: 24px; text-align: center; }
      .stat-item { margin: 0; }
      .stat-number { font-size: 22px; }
      .stat-label { font-size: 10px; margin-top: 2px; }

      /* === ALL SECTIONS === */
      .section-employe, .section-borne, .section-prix, .section-form, .section-metiers {
        padding: 40px 20px;
      }
      .section-header { display: flex; flex-direction: column; align-items: center; margin-bottom: 28px; }
      .section-badge { font-size: 11px; padding: 6px 14px; margin-bottom: 14px; }
      .section-header h2 { font-size: 24px; letter-spacing: -0.8px; width: 100%; }
      .section-header p { font-size: 14px; line-height: 1.6; width: 100%; max-width: 100%; }

      /* === AVANTAGES - grille 2 col centree === */
      .avantages-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
      .avantage-card {
        display: flex; flex-direction: column; align-items: center; text-align: center;
        padding: 20px 12px; gap: 8px; border-radius: 14px;
        background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06);
      }
      .avantage-icon { margin-bottom: 0; width: 40px; height: 40px; border-radius: 10px; }
      .avantage-title { font-size: 13px; line-height: 1.3; }
      .avantage-desc { font-size: 11px; color: rgba(255,255,255,0.4); line-height: 1.4; }

      /* === CAROUSEL MOBILE === */
      .carousel-container { border-radius: 16px; margin-bottom: 32px; }
      .carousel-card { height: 300px; border-radius: 16px; }
      .carousel-card-content { padding: 20px; }
      .carousel-card-icon { width: 40px; height: 40px; border-radius: 10px; margin-bottom: 10px; backdrop-filter: none; }
      .carousel-card-title { font-size: 20px; margin-bottom: 6px; }
      .carousel-card-desc { font-size: 13px; line-height: 1.5; }
      .carousel-card-bar { margin-top: 12px; }
      .carousel-nav { gap: 10px; margin-top: 16px; }
      .carousel-arrow { width: 36px; height: 36px; }
      .carousel-dots { gap: 5px; }
      .carousel-dot { width: 8px; height: 8px; }
      /* Allow carousel transition on mobile (it's lightweight) */
      .carousel-track { transition: transform 0.4s ease !important; }

      /* === SECTEURS === */
      .secteurs-section { padding-top: 28px; border-top: 1px solid rgba(255,255,255,0.05); }
      .secteurs-section h3 { font-size: 17px; margin-bottom: 14px; }
      .secteurs-grid { gap: 8px; justify-content: center; }
      .secteur-pill { padding: 8px 14px; font-size: 12px; border-radius: 50px; }

      /* === BORNE === */
      .borne-feature-card {
        display: flex; flex-direction: column; align-items: center; text-align: center;
        padding: 20px 14px; gap: 10px; margin-bottom: 10px; border-radius: 14px;
      }
      .borne-feature-icon { width: 46px; height: 46px; min-width: 46px; border-radius: 12px; }
      .borne-feature-title { font-size: 14px; }
      .borne-feature-desc { font-size: 12px; color: rgba(255,255,255,0.45); }
      .use-cases { text-align: center; }
      .use-cases h3 { font-size: 17px; margin-bottom: 14px; }
      .use-cases-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; }
      .use-case-pill { justify-content: center; padding: 11px 10px; font-size: 12px; border-radius: 10px; }

      /* === PRIX === */
      .prix-comparison { gap: 12px; }
      .prix-card { padding: 24px 18px; text-align: center; border-radius: 18px; }
      .prix-card-label { font-size: 11px; text-align: center; }
      .prix-card-amount { font-size: 38px; margin-bottom: 18px; }
      .prix-card-amount span { font-size: 13px; }
      .prix-card-badge { font-size: 11px; padding: 5px 14px; top: -10px; }
      .prix-list { margin-bottom: 18px; }
      .prix-list li { font-size: 13px; padding: 7px 0; }
      .prix-economie { padding: 14px; border-radius: 10px; margin-bottom: 16px; }
      .prix-economie strong { font-size: 18px; }
      .prix-vs { font-size: 20px; padding: 4px 0; }
      .prix-bornii .cta-btn { width: 100%; justify-content: center; }

      /* === FORM === */
      .form-layout { gap: 28px; }
      .form-left { text-align: center; }
      .form-left h2 { font-size: 22px; letter-spacing: -0.5px; }
      .form-left > p { font-size: 14px; }
      .form-guarantees { display: inline-flex; flex-direction: column; align-items: flex-start; text-align: left; }
      .guarantee-item { font-size: 13px; padding: 6px 0; gap: 10px; }
      .contact-buttons { flex-direction: column; align-items: stretch; }
      .whatsapp-btn, .phone-btn { justify-content: center; padding: 12px 16px; border-radius: 10px; }
      .form-card { padding: 24px 18px; border-radius: 18px; border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.03); }
      .form-header { margin-bottom: 20px; }
      .form-header h2 { font-size: 20px; }
      .form-header p { font-size: 13px; }
      .input-group { margin-bottom: 14px; }
      .input-group label { font-size: 12px; margin-bottom: 6px; }
      .input-group input, .input-group textarea { padding: 13px 14px; font-size: 15px; border-radius: 10px; }
      .input-row { flex-direction: column; gap: 0; }
      .submit-btn { padding: 15px; font-size: 15px; border-radius: 12px; box-shadow: none; margin-top: 4px; }
      .disclaimer { font-size: 10px; margin-top: 12px; }
      .error-msg { font-size: 12px; padding: 10px 12px; }

      /* === FOOTER === */
      .bornii-footer { padding: 28px 20px; }
      .footer-inner { flex-direction: column; text-align: center; gap: 14px; }
      .footer-brand .logo { justify-content: center; }
      .footer-brand p { font-size: 12px; }
      .footer-links { justify-content: center; flex-wrap: wrap; gap: 16px; }
      .footer-links a { font-size: 13px; }
      .footer-legal { text-align: center; }
      .footer-legal p { font-size: 10px; }

      /* === SUCCESS === */
      /* === CHATBOT MOBILE === */
      .chatbot-widget {
        bottom: 0;
        right: 0;
        left: 0;
        width: 100%;
        max-height: 70vh;
        border-radius: 20px 20px 0 0;
        border-bottom: none;
      }
      .chatbot-messages { max-height: 50vh; }
      .chatbot-options { padding-left: 0; }
      .chatbot-msg-bubble { max-width: 85%; }
      .chatbot-fab { bottom: 16px; right: 16px; width: 50px; height: 50px; border-radius: 14px; }

      /* === CHATBOT MODAL MOBILE === */
      .chatbot-modal-overlay { padding: 16px; align-items: center; }
      .chatbot-modal {
        max-width: 100%;
        max-height: 85vh;
        border-radius: 20px;
      }
      .chatbot-modal-header { padding: 16px 18px; }
      .chatbot-modal-avatar { width: 42px; height: 42px; border-radius: 12px; }
      .chatbot-modal-title { font-size: 16px; }
      .chatbot-modal-subtitle { font-size: 12px; }
      .chatbot-modal-body { padding: 18px 16px; max-height: 55vh; min-height: 180px; }
      .chatbot-modal-body .chatbot-msg-bubble { font-size: 13px; padding: 10px 14px; }
      .chatbot-modal-options { padding-left: 0; }
      .chatbot-modal-option-btn { padding: 10px 14px; font-size: 13px; }
      .chatbot-modal-footer { padding: 12px 16px; font-size: 10px; }

      .success-container { padding: 20px; }
      .success-card { padding: 32px 20px; border-radius: 18px; }
      .success-icon { width: 64px; height: 64px; margin-bottom: 20px; }
      .success-card h1 { font-size: 22px; }
      .success-card p { font-size: 14px; }
      .success-info { padding: 16px; }
    }

    /* Small phone */
    @media (max-width: 380px) {
      .hero-title { font-size: 26px; }
      .hero-image-mobile img { max-width: 180px; }
      .price-amount { font-size: 32px; }
      .hero-stats { grid-template-columns: repeat(2, 1fr); gap: 12px; }
      .section-header h2 { font-size: 22px; }
      .carousel-card { height: 260px; }
      .carousel-card-title { font-size: 18px; }
      .carousel-card-desc { font-size: 12px; }
      .carousel-arrow { width: 30px; height: 30px; }
      .carousel-dot { width: 6px; height: 6px; }
      .carousel-dots { gap: 4px; }
      .avantages-grid { grid-template-columns: 1fr; }
      .avantage-card { flex-direction: row; text-align: left; gap: 12px; }
      .use-cases-grid { grid-template-columns: 1fr; }
    }
  `;
}

export default Bornii;
